import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { canAccessBudgetBaseline } from '../../utils/reportAccess';
import { ListenButton } from '../common/AudioGuide';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Permission check component
const AccessDenied = () => (
  <div style={{
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: '#f8d7da',
    border: '1px solid #f5c6cb',
    borderRadius: '8px',
    color: '#721c24'
  }}>
    <h3 style={{ margin: '0 0 0.5rem 0' }}>🔒 Access Denied</h3>
    <p style={{ margin: 0 }}>You don't have permission to view project reports.</p>
  </div>
);

// Disciplines configuration
const DISCIPLINES = [
  { id: 'piping', name: 'Piping', color: '#8b5cf6' },
  { id: 'mechanical', name: 'Mechanical', color: '#3b82f6' },
  { id: 'structural', name: 'Structural', color: '#10b981' },
  { id: 'hvac', name: 'HVAC', color: '#f59e0b' },
  { id: 'ducts', name: 'Ducts', color: '#ef4444' },
  { id: 'boilermaker', name: 'Boilermaker', color: '#ef6c00' },
  { id: 'vessels', name: 'Vessels', color: '#ec4899' },
  { id: 'electrical', name: 'Electrical', color: '#6366f1' },
  { id: 'civil', name: 'Civil', color: '#14b8a6' },
  { id: 'indirect', name: 'Indirect', color: '#64748b' },
];

/**
 * Generate a normal distribution curve for hours over a date range
 */
const generateNormalDistribution = (startDate, finishDate, totalHours) => {
  if (!startDate || !finishDate || !totalHours || totalHours <= 0) return [];
  
  const start = new Date(startDate);
  const end = new Date(finishDate);
  const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  
  if (daysDiff <= 0) return [];
  
  const weeks = Math.ceil(daysDiff / 7);
  const data = [];
  const mean = weeks / 2;
  const stdDev = weeks / 6;
  
  let rawValues = [];
  let totalRaw = 0;
  
  for (let i = 0; i < weeks; i++) {
    const x = i + 0.5;
    const exponent = -0.5 * Math.pow((x - mean) / stdDev, 2);
    const value = Math.exp(exponent);
    rawValues.push(value);
    totalRaw += value;
  }
  
  let cumulative = 0;
  const currentDate = new Date(start);
  
  for (let i = 0; i < weeks; i++) {
    const periodHours = Math.round((rawValues[i] / totalRaw) * totalHours);
    cumulative += periodHours;
    
    const weekStart = new Date(currentDate);
    currentDate.setDate(currentDate.getDate() + 7);
    
    data.push({
      date: weekStart.toISOString().split('T')[0],
      period: periodHours,
      cumulative: cumulative,
      week: `Week ${i + 1}`
    });
  }
  
  if (data.length > 0) {
    const diff = totalHours - cumulative;
    if (diff !== 0) {
      data[data.length - 1].period += diff;
      data[data.length - 1].cumulative = totalHours;
    }
  }
  
  return data;
};

/**
 * Get all unique weeks across all distributions and combine data
 * Now includes man-weeks calculation based on hoursPerWeek
 */
const combineAllDistributions = (disciplineData, selectedDisciplines, selectedTypes) => {
  // Find overall date range across all disciplines
  let minDate = null;
  let maxDate = null;
  
  DISCIPLINES.forEach(d => {
    const data = disciplineData[d.id];
    if (!data) return;
    
    ['shop', 'field'].forEach(type => {
      if (data[type]?.startDate && data[type]?.finishDate) {
        const start = new Date(data[type].startDate);
        const end = new Date(data[type].finishDate);
        if (!minDate || start < minDate) minDate = start;
        if (!maxDate || end > maxDate) maxDate = end;
      }
    });
  });
  
  if (!minDate || !maxDate) return [];
  
  // Generate weekly periods for the entire range
  const daysDiff = Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24)) + 1;
  const weeks = Math.ceil(daysDiff / 7);
  
  const combined = [];
  const currentDate = new Date(minDate);
  
  // Pre-generate all distributions with hoursPerWeek
  const distributions = {};
  DISCIPLINES.forEach(d => {
    const data = disciplineData[d.id];
    if (!data) return;
    
    ['shop', 'field'].forEach(type => {
      if (data[type]?.startDate && data[type]?.finishDate && data[type]?.hours) {
        distributions[`${d.id}_${type}`] = {
          data: generateNormalDistribution(data[type].startDate, data[type].finishDate, parseFloat(data[type].hours)),
          startDate: new Date(data[type].startDate),
          finishDate: new Date(data[type].finishDate),
          hoursPerWeek: parseFloat(data[type].hoursPerWeek) || 40
        };
      }
    });
  });
  
  for (let i = 0; i < weeks; i++) {
    const weekStart = new Date(currentDate);
    const weekEnd = new Date(currentDate);
    weekEnd.setDate(weekEnd.getDate() + 6);
    currentDate.setDate(currentDate.getDate() + 7);
    
    const weekData = {
      date: weekStart.toISOString().split('T')[0],
      week: `Week ${i + 1}`,
    };
    
    let totalPeriod = 0;
    let totalCumulative = 0;
    let shopPeriod = 0;
    let shopCumulative = 0;
    let fieldPeriod = 0;
    let fieldCumulative = 0;
    
    // For man-weeks calculations
    let totalManWeeks = 0;
    let shopManWeeks = 0;
    let fieldManWeeks = 0;
    
    // For each distribution, find which week index this global week corresponds to
    selectedDisciplines.forEach(discId => {
      ['shop', 'field'].forEach(type => {
        if (!selectedTypes.includes(type)) return;
        
        const key = `${discId}_${type}`;
        const dist = distributions[key];
        
        if (dist && dist.data.length > 0) {
          // Calculate the week index relative to this distribution's start
          const distWeekIndex = Math.floor((weekStart - dist.startDate) / (7 * 24 * 60 * 60 * 1000));
          
          if (distWeekIndex >= 0 && distWeekIndex < dist.data.length) {
            const value = dist.data[distWeekIndex];
            const hoursPerWeek = dist.hoursPerWeek;
            const menNeeded = value.period / hoursPerWeek;
            
            weekData[`${key}_period`] = value.period;
            weekData[`${key}_cumulative`] = value.cumulative;
            weekData[`${key}_manweeks`] = menNeeded;
            
            if (type === 'shop') {
              shopPeriod += value.period;
              shopCumulative += value.cumulative;
              shopManWeeks += menNeeded;
            } else {
              fieldPeriod += value.period;
              fieldCumulative += value.cumulative;
              fieldManWeeks += menNeeded;
            }
            totalPeriod += value.period;
            totalCumulative += value.cumulative;
            totalManWeeks += menNeeded;
          }
        }
      });
    });
    
    weekData.total_period = totalPeriod;
    weekData.total_cumulative = totalCumulative;
    weekData.shop_period = shopPeriod;
    weekData.shop_cumulative = shopCumulative;
    weekData.field_period = fieldPeriod;
    weekData.field_cumulative = fieldCumulative;
    
    // Man-weeks data
    weekData.total_manweeks = totalManWeeks;
    weekData.shop_manweeks = shopManWeeks;
    weekData.field_manweeks = fieldManWeeks;
    
    combined.push(weekData);
  }
  
  // Calculate running cumulative totals (for hours)
  let runningShop = 0;
  let runningField = 0;
  let runningTotal = 0;
  
  // Running cumulative man-weeks (total men accumulated over time)
  let runningShopMen = 0;
  let runningFieldMen = 0;
  let runningTotalMen = 0;
  
  combined.forEach(week => {
    runningShop += week.shop_period;
    runningField += week.field_period;
    runningTotal += week.total_period;
    week.shop_cumulative = runningShop;
    week.field_cumulative = runningField;
    week.total_cumulative = runningTotal;
    
    // Cumulative man-weeks (total worker-weeks accumulated)
    runningShopMen += week.shop_manweeks;
    runningFieldMen += week.field_manweeks;
    runningTotalMen += week.total_manweeks;
    week.shop_manweeks_cumulative = runningShopMen;
    week.field_manweeks_cumulative = runningFieldMen;
    week.total_manweeks_cumulative = runningTotalMen;
  });
  
  return combined;
};

// Checkbox Slicer Component
const SlicerSection = ({ title, icon, items, selectedItems, onToggle, onSelectAll, colorKey }) => {
  const allSelected = items.every(item => selectedItems.includes(item.id));
  
  return (
    <div style={{
      background: 'white',
      padding: '1rem',
      borderRadius: '10px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      border: '1px solid rgba(0,0,0,0.06)',
      minWidth: '180px'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '0.75rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <span style={{ fontWeight: '600', color: '#374151', fontSize: '0.9rem' }}>
          {icon} {title}
        </span>
        <button
          onClick={onSelectAll}
          style={{
            padding: '0.25rem 0.5rem',
            fontSize: '0.7rem',
            background: allSelected ? '#e5e7eb' : '#8b5cf6',
            color: allSelected ? '#6b7280' : 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {allSelected ? 'Clear' : 'All'}
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '200px', overflowY: 'auto' }}>
        {items.map(item => (
          <label
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.35rem 0.5rem',
              borderRadius: '6px',
              cursor: 'pointer',
              background: selectedItems.includes(item.id) ? '#f3f4f6' : 'transparent',
              transition: 'background 0.15s'
            }}
          >
            <input
              type="checkbox"
              checked={selectedItems.includes(item.id)}
              onChange={() => onToggle(item.id)}
              style={{ 
                width: '16px', 
                height: '16px',
                accentColor: colorKey ? item[colorKey] : '#8b5cf6',
                cursor: 'pointer'
              }}
            />
            {colorKey && item[colorKey] && (
              <span style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: item[colorKey]
              }} />
            )}
            <span style={{ 
              fontSize: '0.85rem', 
              color: selectedItems.includes(item.id) ? '#1f2937' : '#6b7280',
              fontWeight: selectedItems.includes(item.id) ? '500' : '400'
            }}>
              {item.name}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
};

const BudgetReport = ({ jobNumber }) => {
  const { instance, accounts } = useMsal();
  
  // Permission states
  const [hasPermission, setHasPermission] = useState(null);
  const [permissionLoading, setPermissionLoading] = useState(true);
  
  // Loading and saving states
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [saveError, setSaveError] = useState(null);
  
  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const response = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (response.data.success) {
          setHasPermission(canAccessBudgetBaseline(response.data.data));
        } else {
          setHasPermission(false);
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        setHasPermission(false);
      } finally {
        setPermissionLoading(false);
      }
    };
    checkPermissions();
  }, [instance, accounts]);
  
  // Slicer selections
  const [selectedDisciplines, setSelectedDisciplines] = useState(DISCIPLINES.map(d => d.id));
  const [selectedTypes, setSelectedTypes] = useState(['shop', 'field']);
  
  // Discipline data - each has shop and field with their own dates, hours, and hours per week
  const [disciplineData, setDisciplineData] = useState(
    DISCIPLINES.reduce((acc, d) => ({
      ...acc,
      [d.id]: {
        shop: { startDate: '', finishDate: '', hours: '', hoursPerWeek: 40 },
        field: { startDate: '', finishDate: '', hours: '', hoursPerWeek: 40 }
      }
    }), {})
  );
  
  // View mode: 'hours' or 'manweeks'
  const [viewMode, setViewMode] = useState('hours');
  
  // Phase selection: 'estimation' (Phase 1) or 'execution' (Phase 2)
  const [selectedPhase, setSelectedPhase] = useState('estimation');
  
  // Phase 2 (Execution) data from schedule_baseline_1
  const [phase2CurveData, setPhase2CurveData] = useState([]);
  
  
  // Expanded rows for showing date inputs
  const [expandedRows, setExpandedRows] = useState({});
  
  // Ref to track if data has been modified (to prevent saving on initial load)
  const hasBeenModified = useRef(false);
  const saveTimeoutRef = useRef(null);
  
  // Phase 2 discipline data (grouped by discipline and shop/field)
  const [phase2DisciplineData, setPhase2DisciplineData] = useState({});

  // Version comparison state
  const [availableVersions, setAvailableVersions] = useState([]);
  const [compareVersionIds, setCompareVersionIds] = useState([]);
  const [versionCurveData, setVersionCurveData] = useState({});
  const [showVersionDropdown, setShowVersionDropdown] = useState(false);
  const [liveHistogramMeta, setLiveHistogramMeta] = useState(null);
  const [maestroUploading, setMaestroUploading] = useState(false);
  const [phase2Loading, setPhase2Loading] = useState(false);
  const [phase2Error, setPhase2Error] = useState(null);
  const maestroFileRef = useRef(null);
  
  // Load Phase 2 (Execution): prefer live P6 Resource Histogram, fall back to schedule_baseline Excel columns
  const loadPhase2Data = useCallback(async (job, disciplines = [], types = []) => {
    if (!job) return;
    
    setPhase2Loading(true);
    setPhase2Error(null);
    try {
      // 1) Live P6 weekly schedule update + proposal (can take ~10–20s)
      try {
        const liveRes = await authenticatedApiCall(
          instance,
          accounts,
          'GET',
          `/resource-histogram/${job}`,
          null,
          { timeout: 120000 }
        );
        if (liveRes.data?.success && liveRes.data.phase2?.curve?.length) {
          setPhase2CurveData(liveRes.data.phase2.curve);
          setPhase2DisciplineData(liveRes.data.phase2.disciplineGrouped || {});
          setLiveHistogramMeta({
            ...(liveRes.data.meta || {}),
            estimateNumber: liveRes.data.estimateNumber,
            source: 'p6-live'
          });
          setSelectedPhase((prev) => (prev === 'estimation' ? 'execution' : prev));
          return;
        }
        if (liveRes.data?.success === false) {
          setPhase2Error(liveRes.data.error || 'Live P6 histogram returned no curve');
        }
      } catch (liveErr) {
        const status = liveErr.response?.status;
        const msg = liveErr.response?.data?.error || liveErr.message || 'Live P6 unavailable';
        console.warn('Live P6 histogram unavailable, falling back to schedule baseline:', msg);
        // 404 = route not deployed yet / no estimate mapping
        setPhase2Error(status === 404
          ? (liveErr.response?.data?.error || 'Live histogram API not available (restart backend / deploy, or map estimate↔job).')
          : msg);
      }

      setLiveHistogramMeta(null);

      // 2) Legacy Excel-filled schedule_baseline_1 weekly columns
      const params = new URLSearchParams();
      if (disciplines.length > 0) {
        const disciplineNames = disciplines.map(id => 
          DISCIPLINES.find(d => d.id === id)?.name || id
        );
        params.append('disciplines', disciplineNames.join(','));
      }
      if (types.length > 0) {
        params.append('types', types.join(','));
      }
      const queryString = params.toString();
      const curveUrl = `/schedule-baseline/${job}/curve${queryString ? `?${queryString}` : ''}`;
      const groupedUrl = `/schedule-baseline/${job}/curve-by-discipline`;
      
      const [curveResponse, groupedResponse] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', curveUrl),
        authenticatedApiCall(instance, accounts, 'GET', groupedUrl)
      ]);
      
      const curve = curveResponse.data?.success ? (curveResponse.data.data || []) : [];
      setPhase2CurveData(curve);
      if (curve.length) setPhase2Error(null);
      
      if (groupedResponse.data?.success && groupedResponse.data.data) {
        setPhase2DisciplineData(groupedResponse.data.data);
      }
    } catch (error) {
      console.error('Error loading Phase 2 curve data:', error);
      setPhase2Error(error.message || 'Failed to load Phase 2 data');
    } finally {
      setPhase2Loading(false);
    }
  }, [instance, accounts]);

  const uploadMaestroActuals = useCallback(async (file) => {
    if (!jobNumber || !file) return;
    try {
      setMaestroUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/resource-histogram/${jobNumber}/maestro-actuals`,
        formData
      );
      await loadPhase2Data(jobNumber, selectedDisciplines, selectedTypes);
    } catch (err) {
      console.error('Maestro actuals import failed:', err);
      alert(err.response?.data?.error || err.message || 'Maestro import failed');
    } finally {
      setMaestroUploading(false);
      if (maestroFileRef.current) maestroFileRef.current.value = '';
    }
  }, [instance, accounts, jobNumber, loadPhase2Data, selectedDisciplines, selectedTypes]);

  const fetchVersions = useCallback(async (job) => {
    if (!job) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/schedule-baseline/${job}/versions`);
      if (response.data.success) setAvailableVersions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching versions for comparison:', err);
    }
  }, [instance, accounts]);

  const loadVersionCurves = useCallback(async (job, ids) => {
    if (!job || ids.length === 0) { setVersionCurveData({}); return; }
    try {
      const response = await authenticatedApiCall(instance, accounts, 'GET',
        `/schedule-baseline/${job}/versions/compare?ids=${ids.join(',')}`);
      if (response.data.success) setVersionCurveData(response.data.data || {});
    } catch (err) {
      console.error('Error loading version curves:', err);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (jobNumber && compareVersionIds.length > 0) {
      loadVersionCurves(jobNumber, compareVersionIds);
    } else {
      setVersionCurveData({});
    }
  }, [jobNumber, compareVersionIds, loadVersionCurves]);

  // Load data from API when job changes
  const loadData = useCallback(async (job) => {
    if (!job) return;
    
    try {
      setIsLoading(true);
      setSaveError(null);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/budget-baseline/${job}`
      );
      
      if (response.data.success && response.data.data) {
        // Merge loaded data with default structure
        const loadedData = response.data.data;
        setDisciplineData(prev => {
          const merged = { ...prev };
          DISCIPLINES.forEach(d => {
            if (loadedData[d.id]) {
              merged[d.id] = {
                shop: { 
                  startDate: '', finishDate: '', hours: '', hoursPerWeek: 40,
                  ...loadedData[d.id].shop 
                },
                field: { 
                  startDate: '', finishDate: '', hours: '', hoursPerWeek: 40,
                  ...loadedData[d.id].field 
                }
              };
            }
          });
          return merged;
        });
      }
    } catch (error) {
      console.error('Error loading budget baseline:', error);
      setSaveError('Failed to load data');
    } finally {
      setIsLoading(false);
      hasBeenModified.current = false;
    }
  }, [instance, accounts]);
  
  // Save data to API
  const saveData = useCallback(async (data, job) => {
    if (!job) return;
    
    try {
      setIsSaving(true);
      setSaveError(null);
      
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'POST',
        `/budget-baseline/${job}/bulk`,
        { data }
      );
      
      if (response.data.success) {
        setLastSaved(new Date());
      } else {
        setSaveError('Failed to save');
      }
    } catch (error) {
      console.error('Error saving budget baseline:', error);
      setSaveError('Failed to save');
    } finally {
      setIsSaving(false);
    }
  }, [instance, accounts]);
  
  // Load data when job changes
  useEffect(() => {
    if (jobNumber) {
      loadData(jobNumber);
      loadPhase2Data(jobNumber, selectedDisciplines, selectedTypes);
      fetchVersions(jobNumber);
    }
  }, [jobNumber, loadData, loadPhase2Data, fetchVersions, selectedDisciplines, selectedTypes]);
  
  // Auto-save with debounce when data changes
  useEffect(() => {
    if (!hasBeenModified.current || !jobNumber) return;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout to save after 1 second of no changes
    saveTimeoutRef.current = setTimeout(() => {
      saveData(disciplineData, jobNumber);
    }, 1000);
    
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [disciplineData, jobNumber, saveData]);
  
  // Toggle discipline selection
  const toggleDiscipline = (id) => {
    setSelectedDisciplines(prev => 
      prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]
    );
  };
  
  // Toggle type selection
  const toggleType = (id) => {
    setSelectedTypes(prev => 
      prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]
    );
  };
  
  // Select all disciplines
  const selectAllDisciplines = () => {
    if (selectedDisciplines.length === DISCIPLINES.length) {
      setSelectedDisciplines([]);
    } else {
      setSelectedDisciplines(DISCIPLINES.map(d => d.id));
    }
  };
  
  // Select all types
  const selectAllTypes = () => {
    if (selectedTypes.length === 2) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(['shop', 'field']);
    }
  };
  
  // Toggle row expansion
  const toggleRowExpanded = (discId) => {
    setExpandedRows(prev => ({
      ...prev,
      [discId]: !prev[discId]
    }));
  };
  
  // Update discipline data
  const updateDisciplineData = (discId, type, field, value) => {
    hasBeenModified.current = true;
    setDisciplineData(prev => ({
      ...prev,
      [discId]: {
        ...prev[discId],
        [type]: {
          ...prev[discId][type],
          [field]: value
        }
      }
    }));
  };
  
  // Combined chart data based on selections and phase
  const chartData = useMemo(() => {
    if (selectedPhase === 'execution' && Object.keys(phase2DisciplineData).length > 0) {
      // Use Phase 2 (Execution) data from schedule_baseline_1
      // Build per-discipline chart data from phase2DisciplineData
      
      // First, find all unique dates across all discipline curves
      const allDates = new Set();
      Object.values(phase2DisciplineData).forEach(discData => {
        ['shop', 'field'].forEach(type => {
          if (discData[type]?.curveData) {
            discData[type].curveData.forEach(item => allDates.add(item.date));
          }
        });
      });
      
      const sortedDates = Array.from(allDates).sort();
      if (sortedDates.length === 0) return [];
      
      // Build period hours per date first. Do NOT sum per-discipline budget_cumulative
      // values — curves are trimmed at different end dates, so that makes the total
      // cumulative line drop at the end. Rebuild running totals from period hours.
      const weeks = sortedDates.map((date, index) => {
        const weekData = {
          date,
          week: `Week ${index + 1}`,
        };
        
        let totalPeriod = 0;
        let shopPeriod = 0;
        let fieldPeriod = 0;
        
        DISCIPLINES.forEach(disc => {
          const discKey = disc.id;
          const scheduleData = phase2DisciplineData[discKey] || phase2DisciplineData[disc.name.toLowerCase()];
          
          if (!scheduleData) return;
          if (!selectedDisciplines.includes(discKey)) return;
          
          ['shop', 'field'].forEach(type => {
            if (!selectedTypes.includes(type)) return;
            
            const curveData = scheduleData[type]?.curveData;
            if (!curveData) return;
            
            const dataPoint = curveData.find(item => item.date === date);
            const period = dataPoint ? (Number(dataPoint.budget_period) || 0) : 0;
            const key = `${discKey}_${type}`;
            weekData[`${key}_period`] = period;
            weekData[`${key}_manweeks`] = period / 40;
            
            if (type === 'shop') shopPeriod += period;
            else fieldPeriod += period;
            totalPeriod += period;
          });
        });
        
        weekData.total_period = totalPeriod;
        weekData.shop_period = shopPeriod;
        weekData.field_period = fieldPeriod;
        weekData.total_manweeks = totalPeriod / 40;
        weekData.shop_manweeks = shopPeriod / 40;
        weekData.field_manweeks = fieldPeriod / 40;
        
        return weekData;
      });

      const runningByKey = {};
      let runningTotal = 0;
      let runningShop = 0;
      let runningField = 0;
      return weeks.map((week) => {
        runningTotal += week.total_period || 0;
        runningShop += week.shop_period || 0;
        runningField += week.field_period || 0;
        week.total_cumulative = runningTotal;
        week.shop_cumulative = runningShop;
        week.field_cumulative = runningField;
        DISCIPLINES.forEach((disc) => {
          ['shop', 'field'].forEach((type) => {
            const key = `${disc.id}_${type}`;
            const periodKey = `${key}_period`;
            if (week[periodKey] == null) return;
            runningByKey[key] = (runningByKey[key] || 0) + (week[periodKey] || 0);
            week[`${key}_cumulative`] = runningByKey[key];
          });
        });
        return week;
      });
    }
    // Use Phase 1 (Estimation) data
    return combineAllDistributions(disciplineData, selectedDisciplines, selectedTypes);
  }, [disciplineData, selectedDisciplines, selectedTypes, selectedPhase, phase2DisciplineData]);

  // Merge version comparison curves into chart data
  const chartDataWithVersions = useMemo(() => {
    if (selectedPhase !== 'execution' || Object.keys(versionCurveData).length === 0) return chartData;

    const dateMap = {};
    chartData.forEach(d => { dateMap[d.date] = { ...d }; });

    Object.entries(versionCurveData).forEach(([vId, curve]) => {
      curve.forEach(pt => {
        if (!dateMap[pt.date]) {
          dateMap[pt.date] = { date: pt.date };
        }
        dateMap[pt.date][`version_${vId}_cumulative`] = pt.budget_cumulative;
        dateMap[pt.date][`version_${vId}_period`] = pt.budget_period;
      });
    });

    return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [chartData, versionCurveData, selectedPhase]);

  // Get effective discipline data based on selected phase
  // For Phase 2, this merges schedule data into the discipline grid
  const effectiveDisciplineData = useMemo(() => {
    if (selectedPhase !== 'execution' || Object.keys(phase2DisciplineData).length === 0) {
      return disciplineData;
    }
    
    // Merge Phase 2 schedule data into discipline data format
    const merged = { ...disciplineData };
    
    DISCIPLINES.forEach(disc => {
      const scheduleData = phase2DisciplineData[disc.id] || phase2DisciplineData[disc.name.toLowerCase()];
      
      if (scheduleData) {
        merged[disc.id] = {
          shop: {
            ...merged[disc.id]?.shop,
            hours: scheduleData.shop?.hours || 0,
            startDate: merged[disc.id]?.shop?.startDate || '',
            finishDate: merged[disc.id]?.shop?.finishDate || '',
            hoursPerWeek: merged[disc.id]?.shop?.hoursPerWeek || 40,
          },
          field: {
            ...merged[disc.id]?.field,
            hours: scheduleData.field?.hours || 0,
            startDate: merged[disc.id]?.field?.startDate || '',
            finishDate: merged[disc.id]?.field?.finishDate || '',
            hoursPerWeek: merged[disc.id]?.field?.hoursPerWeek || 40,
          }
        };
      }
    });
    
    return merged;
  }, [disciplineData, phase2DisciplineData, selectedPhase]);
  
  // Calculate totals based on selections and phase
  const totals = useMemo(() => {
    let totalShop = 0;
    let totalField = 0;
    
    selectedDisciplines.forEach(dId => {
      if (selectedTypes.includes('shop')) {
        totalShop += parseFloat(effectiveDisciplineData[dId]?.shop?.hours) || 0;
      }
      if (selectedTypes.includes('field')) {
        totalField += parseFloat(effectiveDisciplineData[dId]?.field?.hours) || 0;
      }
    });
    
    return { shop: totalShop, field: totalField, total: totalShop + totalField };
  }, [effectiveDisciplineData, selectedDisciplines, selectedTypes]);

  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    return parseFloat(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  
  const formatDateShort = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const clearAll = async () => {
    if (!window.confirm('Are you sure you want to clear all budget baseline data for this job?')) {
      return;
    }
    
    hasBeenModified.current = true;
    setDisciplineData(
      DISCIPLINES.reduce((acc, d) => ({
        ...acc,
        [d.id]: {
          shop: { startDate: '', finishDate: '', hours: '', hoursPerWeek: 40 },
          field: { startDate: '', finishDate: '', hours: '', hoursPerWeek: 40 }
        }
      }), {})
    );
    
    // Also delete from database
    if (jobNumber) {
      try {
        await authenticatedApiCall(
          instance,
          accounts,
          'DELETE',
          `/budget-baseline/${jobNumber}`
        );
        setLastSaved(new Date());
      } catch (error) {
        console.error('Error clearing data:', error);
        setSaveError('Failed to clear data');
      }
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const unit = viewMode === 'hours' ? 'hrs' : 'men';
      return (
        <div style={{
          background: 'white',
          padding: '12px',
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          maxWidth: '350px'
        }}>
          <p style={{ fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
            {formatDate(label)}
          </p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '3px 0', fontSize: '0.8rem' }}>
              {entry.name}: {viewMode === 'hours' ? formatNumber(entry.value) : entry.value.toFixed(1)} {unit}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const inputStyle = {
    width: '100%',
    padding: '0.4rem 0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '5px',
    fontSize: '0.8rem',
    outline: 'none',
    boxSizing: 'border-box'
  };

  // Type items for slicer
  const typeItems = [
    { id: 'shop', name: 'Shop', color: '#f97316' },
    { id: 'field', name: 'Field', color: '#3b82f6' }
  ];

  // Permission check - show loading or access denied
  if (permissionLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: '#6b7280' }}>Checking permissions...</p>
      </div>
    );
  }

  if (!hasPermission) {
    return <AccessDenied />;
  }

  if (!jobNumber) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ color: '#6b7280' }}>No project selected</h2>
        <p style={{ color: '#9ca3af' }}>Please select a project from the dashboard to view the Budget Report.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: '1600px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <h1 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', margin: 0 }}>
                📊 Budget Report
              </h1>
              <ListenButton compact guideId="budget-report-help" title="Budget Report Help" accent="#1d4ed8" />
            </div>
            <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
              Visualize budgeted hours over time by discipline with Shop and Field breakdown
            </p>
          </div>
          {selectedPhase === 'execution' && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                ref={maestroFileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadMaestroActuals(f);
                }}
              />
              <button
                type="button"
                onClick={() => maestroFileRef.current?.click()}
                disabled={maestroUploading}
                style={{
                  padding: '0.5rem 0.9rem',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  background: '#fff',
                  cursor: maestroUploading ? 'wait' : 'pointer',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#334155'
                }}
                title="Upload Maestro raw labour export (Date / Project / Activity / Time)"
              >
                {maestroUploading ? 'Uploading…' : '⬆ Import Maestro Actuals'}
              </button>
            </div>
          )}
        </div>
        {liveHistogramMeta?.weeklyScheduleUpdate && selectedPhase === 'execution' && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.65rem 0.9rem',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            fontSize: '0.82rem',
            color: '#1e3a8a',
            lineHeight: 1.45
          }}>
            <strong>Live P6 execution schedule:</strong>{' '}
            {liveHistogramMeta.weeklyScheduleUpdate.projShortName}
            {liveHistogramMeta.proposalProject ? (
              <> · <strong>Proposal baseline:</strong> {liveHistogramMeta.proposalProject.projShortName}</>
            ) : null}
            {liveHistogramMeta.remainingSpreadMode ? (
              <> · Spread: {liveHistogramMeta.remainingSpreadMode === 'financial_periods' ? 'financial periods' : 'resource curve'}</>
            ) : null}
            {liveHistogramMeta.lastMaestroImport?.imported_at ? (
              <> · Maestro actuals: {new Date(liveHistogramMeta.lastMaestroImport.imported_at).toLocaleString()}</>
            ) : (
              <> · No Maestro actuals imported yet</>
            )}
            <div style={{ marginTop: '0.35rem', color: '#475569' }}>
              P6 dated project IDs are weekly schedule updates (not estimate-phase). Longer term, portal Time Tracker timesheets are intended to become the source of truth for actuals.
            </div>
          </div>
        )}
      </div>

      {/* Save Status Indicator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '1rem',
        fontSize: '0.85rem'
      }}>
        {isLoading && (
          <span style={{ color: '#6b7280' }}>
            ⏳ Loading data...
          </span>
        )}
        {isSaving && (
          <span style={{ color: '#f59e0b' }}>
            💾 Saving...
          </span>
        )}
        {!isLoading && !isSaving && lastSaved && (
          <span style={{ color: '#059669' }}>
            ✓ Saved at {lastSaved.toLocaleTimeString()}
          </span>
        )}
        {saveError && (
          <span style={{ color: '#dc2626' }}>
            ⚠️ {saveError}
          </span>
        )}
      </div>

      {/* Phase 1 Sticky Note */}
      <div style={{
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        border: '1px solid #f59e0b',
        borderLeft: '4px solid #f59e0b',
        borderRadius: '8px',
        padding: '1rem 1.25rem',
        marginBottom: '1.5rem',
        boxShadow: '0 2px 4px rgba(245, 158, 11, 0.15)'
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.25rem' }}>📌</span>
          <div>
            <div style={{ fontWeight: '600', color: '#92400e', marginBottom: '0.25rem' }}>
              Budget Hours Baseline
            </div>
            <div style={{ color: '#a16207', fontSize: '0.875rem', lineHeight: '1.5' }}>
              <strong>Phase 1 (Estimation):</strong> Enter start/finish dates and budget hours per discipline to generate baseline distribution curves.
              <br />
              <strong>Phase 2 (Execution):</strong> Live weekly hours from the latest P6 schedule update (falls back to imported Schedule Baseline if needed).
            </div>
          </div>
        </div>
      </div>

      {/* Discipline Hours Grid */}
      <div style={{
        background: 'white',
        padding: '1.25rem 1.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.06)',
        marginBottom: '1.5rem'
      }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#1f2937', marginBottom: '1rem' }}>
          🎯 Budget Hours by Discipline (click row to expand dates)
        </h3>
        
        {/* Header */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '30px 130px 1fr 1fr 100px',
          gap: '0.75rem',
          marginBottom: '0.5rem',
          paddingBottom: '0.5rem',
          borderBottom: '2px solid #e5e7eb',
          fontSize: '0.8rem',
          fontWeight: '600'
        }}>
          <div></div>
          <div style={{ color: '#374151' }}>Discipline</div>
          <div style={{ color: '#f97316', textAlign: 'center' }}>🏭 Shop</div>
          <div style={{ color: '#3b82f6', textAlign: 'center' }}>🔧 Field</div>
          <div style={{ color: '#374151', textAlign: 'right' }}>Total</div>
        </div>
        
        {/* Discipline Rows */}
        {DISCIPLINES.map((discipline) => {
          // Use effectiveDisciplineData which includes Phase 2 schedule data when applicable
          const shopData = effectiveDisciplineData[discipline.id]?.shop || {};
          const fieldData = effectiveDisciplineData[discipline.id]?.field || {};
          const shopHrs = parseFloat(shopData.hours) || 0;
          const fieldHrs = parseFloat(fieldData.hours) || 0;
          const total = shopHrs + fieldHrs;
          const isSelected = selectedDisciplines.includes(discipline.id);
          const isExpanded = expandedRows[discipline.id];
          const isPhase2 = selectedPhase === 'execution';
          
          return (
            <div key={discipline.id}>
              {/* Main Row */}
              <div 
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '30px 130px 1fr 1fr 100px',
                  gap: '0.75rem',
                  alignItems: 'center',
                  padding: '0.5rem 0',
                  borderBottom: isExpanded ? 'none' : '1px solid #f3f4f6',
                  opacity: isSelected ? 1 : 0.4,
                  cursor: 'pointer',
                  transition: 'background 0.15s'
                }}
                onClick={() => toggleRowExpanded(discipline.id)}
              >
                <div style={{ 
                  fontSize: '1rem', 
                  color: '#6b7280',
                  textAlign: 'center'
                }}>
                  {isExpanded ? '▼' : '▶'}
                </div>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '0.5rem',
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '0.85rem'
                }}>
                  <span style={{ 
                    width: '12px', 
                    height: '12px', 
                    borderRadius: '3px', 
                    backgroundColor: discipline.color 
                  }} />
                  {discipline.name}
                </div>
                <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#6b7280' }}>
                  {shopHrs > 0 ? (
                    <span>
                      <strong style={{ color: '#f97316' }}>{formatNumber(shopHrs)}</strong>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>
                        ({formatDateShort(shopData.startDate)} - {formatDateShort(shopData.finishDate)})
                      </span>
                    </span>
                  ) : '-'}
                </div>
                <div style={{ textAlign: 'center', fontSize: '0.8rem', color: '#6b7280' }}>
                  {fieldHrs > 0 ? (
                    <span>
                      <strong style={{ color: '#3b82f6' }}>{formatNumber(fieldHrs)}</strong>
                      <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem' }}>
                        ({formatDateShort(fieldData.startDate)} - {formatDateShort(fieldData.finishDate)})
                      </span>
                    </span>
                  ) : '-'}
                </div>
                <div style={{ 
                  textAlign: 'right', 
                  fontWeight: '600', 
                  color: total > 0 ? '#059669' : '#9ca3af',
                  fontSize: '0.85rem'
                }}>
                  {formatNumber(total)}
                </div>
              </div>
              
              {/* Expanded Detail Row */}
              {isExpanded && (
                <div style={{
                  background: '#f9fafb',
                  padding: '1rem',
                  marginBottom: '0.5rem',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                    {/* Shop Section */}
                    <div style={{ 
                      background: '#fff7ed', 
                      padding: '1rem', 
                      borderRadius: '8px',
                      border: '1px solid #fed7aa'
                    }}>
                      <div style={{ fontWeight: '600', color: '#c2410c', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                        🏭 Shop Work
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#9a3412', marginBottom: '0.25rem' }}>
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={shopData.startDate || ''}
                            onChange={(e) => updateDisciplineData(discipline.id, 'shop', 'startDate', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#9a3412', marginBottom: '0.25rem' }}>
                            Finish Date
                          </label>
                          <input
                            type="date"
                            value={shopData.finishDate || ''}
                            onChange={(e) => updateDisciplineData(discipline.id, 'shop', 'finishDate', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#9a3412', marginBottom: '0.25rem' }}>
                            Total Hours {isPhase2 && '(from Schedule)'}
                          </label>
                          <input
                            type="number"
                            value={shopData.hours || ''}
                            onChange={(e) => !isPhase2 && updateDisciplineData(discipline.id, 'shop', 'hours', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="0"
                            readOnly={isPhase2}
                            style={{ 
                              ...inputStyle, 
                              fontWeight: '600',
                              ...(isPhase2 ? { background: '#fef3c7', cursor: 'default' } : {})
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#9a3412', marginBottom: '0.25rem' }}>
                            Hrs/Week (Calendar)
                          </label>
                          <input
                            type="number"
                            value={shopData.hoursPerWeek || 40}
                            onChange={(e) => updateDisciplineData(discipline.id, 'shop', 'hoursPerWeek', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="40"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Field Section */}
                    <div style={{ 
                      background: '#eff6ff', 
                      padding: '1rem', 
                      borderRadius: '8px',
                      border: '1px solid #bfdbfe'
                    }}>
                      <div style={{ fontWeight: '600', color: '#1d4ed8', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
                        🔧 Field Work
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#1e40af', marginBottom: '0.25rem' }}>
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={fieldData.startDate || ''}
                            onChange={(e) => updateDisciplineData(discipline.id, 'field', 'startDate', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#1e40af', marginBottom: '0.25rem' }}>
                            Finish Date
                          </label>
                          <input
                            type="date"
                            value={fieldData.finishDate || ''}
                            onChange={(e) => updateDisciplineData(discipline.id, 'field', 'finishDate', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#1e40af', marginBottom: '0.25rem' }}>
                            Total Hours {isPhase2 && '(from Schedule)'}
                          </label>
                          <input
                            type="number"
                            value={fieldData.hours || ''}
                            onChange={(e) => !isPhase2 && updateDisciplineData(discipline.id, 'field', 'hours', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="0"
                            readOnly={isPhase2}
                            style={{ 
                              ...inputStyle, 
                              fontWeight: '600',
                              ...(isPhase2 ? { background: '#dbeafe', cursor: 'default' } : {})
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '0.75rem', color: '#1e40af', marginBottom: '0.25rem' }}>
                            Hrs/Week (Calendar)
                          </label>
                          <input
                            type="number"
                            value={fieldData.hoursPerWeek || 40}
                            onChange={(e) => updateDisciplineData(discipline.id, 'field', 'hoursPerWeek', e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="40"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Totals Row */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '30px 130px 1fr 1fr 100px',
          gap: '0.75rem',
          alignItems: 'center',
          padding: '0.75rem 0',
          marginTop: '0.5rem',
          borderTop: '2px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          marginLeft: '-1.5rem',
          marginRight: '-1.5rem',
          paddingLeft: '1.5rem',
          paddingRight: '1.5rem'
        }}>
          <div></div>
          <div style={{ fontWeight: '700', color: '#1f2937', fontSize: '0.9rem' }}>TOTAL (filtered)</div>
          <div style={{ textAlign: 'center', fontWeight: '700', color: '#f97316', fontSize: '0.9rem' }}>
            {formatNumber(totals.shop)}
          </div>
          <div style={{ textAlign: 'center', fontWeight: '700', color: '#3b82f6', fontSize: '0.9rem' }}>
            {formatNumber(totals.field)}
          </div>
          <div style={{ textAlign: 'right', fontWeight: '700', color: '#059669', fontSize: '1rem' }}>
            {formatNumber(totals.total)}
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {totals.total > 0 && chartData.length > 0 && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Total Budget</div>
            <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#8b5cf6' }}>
              {formatNumber(totals.total)}
            </div>
          </div>
          {selectedTypes.includes('shop') && (
            <div style={{
              background: 'white',
              padding: '1rem',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Shop Hours</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#f97316' }}>
                {formatNumber(totals.shop)}
              </div>
            </div>
          )}
          {selectedTypes.includes('field') && (
            <div style={{
              background: 'white',
              padding: '1rem',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              border: '1px solid rgba(0,0,0,0.06)'
            }}>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Field Hours</div>
              <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#3b82f6' }}>
                {formatNumber(totals.field)}
              </div>
            </div>
          )}
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Duration</div>
            <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#059669' }}>
              {chartData.length} wks
            </div>
          </div>
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
              {viewMode === 'hours' ? 'Peak Weekly Hours' : 'Peak Men/Week'}
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#dc2626' }}>
              {viewMode === 'hours' 
                ? formatNumber(Math.max(...chartData.map(d => d.total_period || 0)))
                : Math.max(...chartData.map(d => d.total_manweeks || 0)).toFixed(1)
              }
            </div>
          </div>
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>Disciplines</div>
            <div style={{ fontSize: '1.4rem', fontWeight: '700', color: '#6366f1' }}>
              {selectedDisciplines.length}
            </div>
          </div>
        </div>
      )}

      {/* Chart Section */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        border: '1px solid rgba(0,0,0,0.06)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>
            📈 {viewMode === 'hours' ? 'Budget Hours Over Time' : 'Manpower Required Per Week'}
          </h3>
          <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
            Showing: {selectedDisciplines.length} discipline(s), {selectedTypes.join(' & ')} | 
            <span style={{ 
              marginLeft: '0.5rem', 
              padding: '0.2rem 0.5rem', 
              borderRadius: '4px',
              background: viewMode === 'hours' ? '#f3e8ff' : '#ecfdf5',
              color: viewMode === 'hours' ? '#6b21a8' : '#065f46',
              fontWeight: '500'
            }}>
              {viewMode === 'hours' ? 'Man-Hours' : 'Men/Week'}
            </span>
          </div>
        </div>
        
        {chartData.length === 0 || selectedDisciplines.length === 0 || selectedTypes.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#6b7280' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <div style={{ fontWeight: '500', marginBottom: '0.5rem' }}>No data to display</div>
            <div style={{ fontSize: '0.875rem' }}>
              {selectedDisciplines.length === 0 ? 'Select at least one discipline' :
               selectedTypes.length === 0 ? 'Select Shop and/or Field' :
               'Click on a discipline row and enter Start Date, Finish Date, and Hours'}
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={500}>
            <ComposedChart
              data={chartDataWithVersions}
              margin={{ top: 20, right: 50, left: 40, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                style={{ fontSize: '0.7rem' }}
                stroke="#6b7280"
              />
              <YAxis 
                yAxisId="left"
                domain={[0, 'auto']}
                label={{ 
                  value: viewMode === 'hours' ? 'Period Hours' : 'Men Needed', 
                  angle: -90, 
                  position: 'insideLeft',
                  dx: -10,
                  style: { fontSize: '0.75rem', fill: '#6b7280', textAnchor: 'middle' }
                }}
                style={{ fontSize: '0.7rem' }}
                stroke="#6b7280"
                tickFormatter={viewMode === 'manweeks' ? (v) => v.toFixed(1) : undefined}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                label={{ 
                  value: viewMode === 'hours' ? 'Cumulative Hours' : 'Cumulative Man-Weeks', 
                  angle: 90, 
                  position: 'insideRight',
                  dx: 10,
                  style: { fontSize: '0.75rem', fill: '#6b7280', textAnchor: 'middle' }
                }}
                style={{ fontSize: '0.7rem' }}
                stroke="#6b7280"
                tickFormatter={viewMode === 'manweeks' ? (v) => v.toFixed(1) : undefined}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.75rem' }} iconType="square" />
              
              {/* Stacked bars for each selected discipline and type */}
              {selectedDisciplines.map((discId) => {
                const disc = DISCIPLINES.find(d => d.id === discId);
                return (
                  <React.Fragment key={discId}>
                    {selectedTypes.includes('shop') && (
                      <Bar 
                        yAxisId="left"
                        dataKey={viewMode === 'hours' ? `${discId}_shop_period` : `${discId}_shop_manweeks`}
                        stackId="values"
                        fill={disc.color}
                        fillOpacity={0.6}
                        name={`${disc.name} Shop`}
                      />
                    )}
                    {selectedTypes.includes('field') && (
                      <Bar 
                        yAxisId="left"
                        dataKey={viewMode === 'hours' ? `${discId}_field_period` : `${discId}_field_manweeks`}
                        stackId="values"
                        fill={disc.color}
                        fillOpacity={1}
                        name={`${disc.name} Field`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
              
              {/* Cumulative line */}
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey={viewMode === 'hours' ? 'total_cumulative' : 'total_manweeks_cumulative'}
                stroke="#1f2937"
                strokeWidth={3}
                name={viewMode === 'hours' ? 'Total Cumulative Hours' : 'Total Cumulative Man-Weeks'}
                dot={{ r: 2 }}
              />

              {/* Version comparison overlay lines */}
              {selectedPhase === 'execution' && compareVersionIds.map((vId, idx) => {
                const versionColors = ['#dc2626', '#2563eb', '#059669', '#d97706', '#7c3aed', '#db2777'];
                const color = versionColors[idx % versionColors.length];
                const versionMeta = availableVersions.find(v => String(v.id) === String(vId));
                const versionLabel = versionMeta ? `v${versionMeta.version_number} ${versionMeta.label || ''}`.trim() : `Version ${vId}`;
                return (
                  <Line
                    key={`version-${vId}`}
                    yAxisId="right"
                    type="monotone"
                    dataKey={`version_${vId}_cumulative`}
                    stroke={color}
                    strokeWidth={2}
                    strokeDasharray="6 3"
                    name={versionLabel}
                    dot={false}
                    connectNulls
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Filter Slicers - Below the Chart */}
      <div style={{
        background: '#f8fafc',
        padding: '1rem 1.25rem',
        borderRadius: '12px',
        marginTop: '1.5rem',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: '600', color: '#475569', marginBottom: '1rem' }}>
          🎛️ Chart Filters
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-start' }}>
          {/* Discipline Slicer */}
          <SlicerSection
            title="Discipline"
            icon="🔧"
            items={DISCIPLINES}
            selectedItems={selectedDisciplines}
            onToggle={toggleDiscipline}
            onSelectAll={selectAllDisciplines}
            colorKey="color"
          />
          
          {/* Shop/Field Slicer */}
          <SlicerSection
            title="Type"
            icon="🏭"
            items={typeItems}
            selectedItems={selectedTypes}
            onToggle={toggleType}
            onSelectAll={selectAllTypes}
            colorKey="color"
          />
          
          {/* View Mode Toggle */}
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '10px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            minWidth: '180px'
          }}>
            <div style={{ 
              fontWeight: '600', 
              color: '#374151', 
              fontSize: '0.9rem',
              marginBottom: '0.75rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              📊 Chart View
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                background: viewMode === 'hours' ? '#f3e8ff' : 'transparent',
                border: viewMode === 'hours' ? '1px solid #c084fc' : '1px solid transparent',
                transition: 'all 0.15s'
              }}>
                <input
                  type="radio"
                  name="viewMode"
                  value="hours"
                  checked={viewMode === 'hours'}
                  onChange={() => setViewMode('hours')}
                  style={{ accentColor: '#8b5cf6', cursor: 'pointer' }}
                />
                <span style={{ 
                  fontSize: '0.85rem', 
                  color: viewMode === 'hours' ? '#6b21a8' : '#6b7280',
                  fontWeight: viewMode === 'hours' ? '600' : '400'
                }}>
                  Man-Hours
                </span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                background: viewMode === 'manweeks' ? '#ecfdf5' : 'transparent',
                border: viewMode === 'manweeks' ? '1px solid #34d399' : '1px solid transparent',
                transition: 'all 0.15s'
              }}>
                <input
                  type="radio"
                  name="viewMode"
                  value="manweeks"
                  checked={viewMode === 'manweeks'}
                  onChange={() => setViewMode('manweeks')}
                  style={{ accentColor: '#10b981', cursor: 'pointer' }}
                />
                <span style={{ 
                  fontSize: '0.85rem', 
                  color: viewMode === 'manweeks' ? '#065f46' : '#6b7280',
                  fontWeight: viewMode === 'manweeks' ? '600' : '400'
                }}>
                  Men/Week
                </span>
              </label>
            </div>
          </div>
          
          {/* Phase Toggle */}
          <div style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '10px',
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            border: '1px solid rgba(0,0,0,0.06)',
            minWidth: '200px'
          }}>
            <div style={{ 
              fontWeight: '600', 
              color: '#374151', 
              fontSize: '0.9rem',
              marginBottom: '0.75rem',
              paddingBottom: '0.5rem',
              borderBottom: '1px solid #e5e7eb'
            }}>
              📋 Data Source
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.5rem',
                borderRadius: '6px',
                cursor: 'pointer',
                background: selectedPhase === 'estimation' ? '#fef3c7' : 'transparent',
                border: selectedPhase === 'estimation' ? '1px solid #f59e0b' : '1px solid transparent',
                transition: 'all 0.15s'
              }}>
                <input
                  type="radio"
                  name="phase"
                  value="estimation"
                  checked={selectedPhase === 'estimation'}
                  onChange={() => setSelectedPhase('estimation')}
                  style={{ accentColor: '#f59e0b', cursor: 'pointer' }}
                />
                <span style={{ 
                  fontSize: '0.85rem', 
                  color: selectedPhase === 'estimation' ? '#92400e' : '#6b7280',
                  fontWeight: selectedPhase === 'estimation' ? '600' : '400'
                }}>
                  Phase 1: Estimation
                </span>
              </label>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.4rem 0.5rem',
                borderRadius: '6px',
                cursor: phase2CurveData.length > 0 ? 'pointer' : 'not-allowed',
                background: selectedPhase === 'execution' ? '#dbeafe' : 'transparent',
                border: selectedPhase === 'execution' ? '1px solid #3b82f6' : '1px solid transparent',
                opacity: phase2Loading || phase2CurveData.length > 0 ? 1 : 0.5,
                transition: 'all 0.15s'
              }}>
                <input
                  type="radio"
                  name="phase"
                  value="execution"
                  checked={selectedPhase === 'execution'}
                  onChange={() => phase2CurveData.length > 0 && setSelectedPhase('execution')}
                  disabled={phase2CurveData.length === 0}
                  style={{ accentColor: '#3b82f6', cursor: phase2CurveData.length > 0 ? 'pointer' : 'not-allowed' }}
                />
                <span style={{ 
                  fontSize: '0.85rem', 
                  color: selectedPhase === 'execution' ? '#1e40af' : '#6b7280',
                  fontWeight: selectedPhase === 'execution' ? '600' : '400'
                }}>
                  Phase 2: Execution{' '}
                  {phase2Loading
                    ? '(Loading P6…)'
                    : phase2CurveData.length === 0
                      ? '(No data)'
                      : ''}
                </span>
              </label>
              {phase2Error && phase2CurveData.length === 0 && !phase2Loading && (
                <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: '#b45309', lineHeight: 1.35 }}>
                  {phase2Error}
                </div>
              )}
            </div>

            {/* Version comparison picker - only when Phase 2 is selected */}
            {selectedPhase === 'execution' && availableVersions.length > 0 && (
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                  Compare Versions
                </div>
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowVersionDropdown(!showVersionDropdown)}
                    style={{
                      width: '100%', padding: '0.4rem 0.6rem', fontSize: '0.8rem',
                      border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer',
                      background: 'white', textAlign: 'left', color: '#374151',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                    }}
                  >
                    <span>{compareVersionIds.length > 0 ? `${compareVersionIds.length} selected` : 'Select versions...'}</span>
                    <span style={{ fontSize: '0.7rem' }}>{showVersionDropdown ? '▲' : '▼'}</span>
                  </button>
                  {showVersionDropdown && (
                    <>
                      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 998 }} onClick={() => setShowVersionDropdown(false)} />
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                        background: 'white', border: '1px solid #d1d5db', borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 999, maxHeight: '200px',
                        overflowY: 'auto'
                      }}>
                        {availableVersions.map(v => {
                          const isSelected = compareVersionIds.includes(String(v.id));
                          return (
                            <label key={v.id} style={{
                              display: 'flex', alignItems: 'center', gap: '0.5rem',
                              padding: '0.4rem 0.6rem', cursor: 'pointer', fontSize: '0.8rem',
                              background: isSelected ? '#eff6ff' : 'transparent',
                              borderBottom: '1px solid #f3f4f6'
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  setCompareVersionIds(prev =>
                                    isSelected ? prev.filter(id => id !== String(v.id)) : [...prev, String(v.id)]
                                  );
                                }}
                                style={{ accentColor: '#3b82f6' }}
                              />
                              <span>
                                v{v.version_number} {v.label || ''}
                                {v.is_baseline && <span style={{ color: '#1e40af', fontWeight: 600, fontSize: '0.7rem' }}> (BASELINE)</span>}
                              </span>
                              <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: '0.7rem' }}>
                                {parseFloat(v.total_hours || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}h
                              </span>
                            </label>
                          );
                        })}
                        {compareVersionIds.length > 0 && (
                          <button
                            onClick={() => { setCompareVersionIds([]); setShowVersionDropdown(false); }}
                            style={{
                              width: '100%', padding: '0.4rem', fontSize: '0.75rem',
                              border: 'none', background: '#fee2e2', color: '#dc2626',
                              cursor: 'pointer', borderRadius: '0 0 8px 8px'
                            }}
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
                {compareVersionIds.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '0.5rem' }}>
                    {compareVersionIds.map((vId, idx) => {
                      const versionColors = ['#dc2626', '#2563eb', '#059669', '#d97706', '#7c3aed', '#db2777'];
                      const color = versionColors[idx % versionColors.length];
                      const v = availableVersions.find(av => String(av.id) === String(vId));
                      return (
                        <span key={vId} style={{
                          fontSize: '0.7rem', padding: '2px 8px', borderRadius: '9999px',
                          background: `${color}15`, color: color, border: `1px solid ${color}40`,
                          display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                          v{v?.version_number || '?'} {v?.label || ''}
                          <button
                            onClick={() => setCompareVersionIds(prev => prev.filter(id => id !== vId))}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color, fontSize: '0.8rem', padding: 0 }}
                          >✕</button>
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <button
            onClick={clearAll}
            style={{
              padding: '0.5rem 1rem',
              background: 'white',
              color: '#dc2626',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              fontSize: '0.85rem',
              fontWeight: '500',
              cursor: 'pointer',
              alignSelf: 'flex-end'
            }}
          >
            Clear All Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetReport;
